import React from 'react';
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Warning24Filled, ErrorCircle24Filled, Info24Filled } from '@fluentui/react-icons';
import { BaseDialog } from './BaseDialog';

type ConfirmationVariant = 'warning' | 'danger' | 'info';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  subtext?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  variant?: ConfirmationVariant;
  maxWidth?: string;
}

const useStyles = makeStyles({
  warningContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    ...shorthands.padding('16px', '24px'),
  },
  warningIcon: {
    fontSize: '48px',
    flexShrink: 0,
  },
  warningText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  warningTitle: {
    fontWeight: '600',
    fontSize: '16px',
    color: tokens.colorNeutralForeground1,
  },
  warningSubtext: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
});

/**
 * ConfirmationDialog - Specialized dialog for confirmation actions
 *
 * Built on top of BaseDialog, provides a consistent UX for confirmation prompts
 * with warning icons and styled action buttons.
 *
 * Variants:
 * - 'warning': Orange warning icon, standard confirm button
 * - 'danger': Red error icon, red confirm button (for destructive actions)
 * - 'info': Blue info icon, standard confirm button
 *
 * Usage:
 * ```tsx
 * <ConfirmationDialog
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   onConfirm={handleDelete}
 *   title="Delete customer"
 *   message="Are you sure you want to delete this customer?"
 *   subtext="This cannot be undone"
 *   variant="danger"
 * />
 * ```
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  subtext,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  variant = 'warning',
  maxWidth = '560px',
}) => {
  const styles = useStyles();

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getIconAndColor = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: <ErrorCircle24Filled className={styles.warningIcon} style={{ color: '#D83B01' }} />,
          buttonStyle: { backgroundColor: '#D83B01', color: 'white' },
        };
      case 'info':
        return {
          icon: <Info24Filled className={styles.warningIcon} style={{ color: tokens.colorBrandForeground1 }} />,
          buttonStyle: {},
        };
      case 'warning':
      default:
        return {
          icon: <Warning24Filled className={styles.warningIcon} style={{ color: tokens.colorPalettePumpkinBorderActive }} />,
          buttonStyle: {},
        };
    }
  };

  const { icon, buttonStyle } = getIconAndColor();

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth={maxWidth}
      showCloseButton={true}
      actions={
        <>
          <Button appearance="secondary" onClick={onClose}>
            {cancelButtonText}
          </Button>
          <Button appearance="primary" style={buttonStyle} onClick={handleConfirm}>
            {confirmButtonText}
          </Button>
        </>
      }
    >
      <div className={styles.warningContent}>
        {icon}
        <div className={styles.warningText}>
          <div className={styles.warningTitle}>{message}</div>
          {subtext && <div className={styles.warningSubtext}>{subtext}</div>}
        </div>
      </div>
    </BaseDialog>
  );
};

export default ConfirmationDialog;
